/** @jsxImportSource @emotion/react */
import React, { memo, useState, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { MenuServiceSubItemType } from '@signal/core/menuService/types'
import Typography from '@signal/components/Typography'
import withCommonSnapshotsOnly from '@signal/hoc/withCommonSnapshotsOnly'
import stripDashesFromAnyGuids from '@signal/utils/stripDashesFromGuid'
import stripQueryParams from '@signal/utils/stripQueryParams'
import {
  PopupContainer,
  PopupHeader,
  PopupSubItemsContainer,
  PopupSubItem
} from './styles'
import { SubItemArrowContainer } from '../MenuItem/styles'
import { ArrowForwardIos } from '@material-ui/icons'
import { followMenuLink, getMenuItemHref, preventLinkFollow } from '../utils'
import { tooltipZIndexSelector } from '@signal/theme/selectors'
import { useTheme } from '@material-ui/core/styles'

const PopupHeaderWithChildren = PopupHeader as React.ComponentType<
  React.PropsWithChildren<
    React.HTMLAttributes<HTMLDivElement> & { $hasSubItems?: boolean }
  >
>

const PopupSubItemWithChildren = PopupSubItem as React.ComponentType<
  React.PropsWithChildren<
    React.AnchorHTMLAttributes<HTMLAnchorElement> & { $isActive?: boolean }
  >
>

const SubItemArrowContainerWithChildren =
  SubItemArrowContainer as React.ComponentType<
    React.PropsWithChildren<
      React.HTMLAttributes<HTMLDivElement> & {
        $hasSubItems?: boolean
        $isOpen?: boolean
        $isParentActive?: boolean
      }
    >
  >

interface HoverPopupProps {
  isVisible: boolean
  name: string
  subMenuItems?: Array<MenuServiceSubItemType>
  onSubItemClick?: (link: string) => void
  currentPathname?: string
  getProcessedLink?: (link: string) => string
  position: { top: number; left: number }
  onMouseEnter?: () => void
  onMouseLeave?: () => void
  searchExpandedSubItems?: Set<string>
}

const HoverPopup: React.FC<HoverPopupProps> = ({
  isVisible,
  name,
  subMenuItems = [],
  onSubItemClick,
  currentPathname = '',
  getProcessedLink,
  position,
  onMouseEnter,
  onMouseLeave,
  searchExpandedSubItems = new Set()
}) => {
  // State to track which sub-items are expanded (for third-level items)
  const [expandedSubItems, setExpandedSubItems] = useState<Set<string>>(
    new Set()
  )

  // Determine active sub-menu item (including third-level items)
  const getActiveSubMenuLink = () => {
    if (!currentPathname || subMenuItems.length === 0) return null

    const normalizedPathname = stripDashesFromAnyGuids(currentPathname)

    // First check second-level items
    const exactMatch = subMenuItems.find(
      ({ link }: MenuServiceSubItemType) =>
        normalizedPathname === stripDashesFromAnyGuids(stripQueryParams(link))
    )

    if (exactMatch) return exactMatch.link

    // Then check third-level items
    for (const subItem of subMenuItems) {
      if (subItem.subMenuItems) {
        const thirdLevelMatch = subItem.subMenuItems.find(
          ({ link }: MenuServiceSubItemType) =>
            normalizedPathname ===
            stripDashesFromAnyGuids(stripQueryParams(link))
        )
        if (thirdLevelMatch) return thirdLevelMatch.link
      }
    }

    // Then look for path that starts with any link (second level)
    const startsWithMatch = subMenuItems.find(
      ({ link }: MenuServiceSubItemType) =>
        normalizedPathname.startsWith(
          stripDashesFromAnyGuids(stripQueryParams(link))
        )
    )

    if (startsWithMatch) return startsWithMatch.link

    // Finally check third-level items for starts-with match
    for (const subItem of subMenuItems) {
      if (subItem.subMenuItems) {
        const thirdLevelStartsWithMatch = subItem.subMenuItems.find(
          ({ link }: MenuServiceSubItemType) =>
            normalizedPathname.startsWith(
              stripDashesFromAnyGuids(stripQueryParams(link))
            )
        )
        if (thirdLevelStartsWithMatch) return thirdLevelStartsWithMatch.link
      }
    }

    return null
  }

  const activeSubMenuLink = getActiveSubMenuLink()

  // Helper to check if a sub-item has an active third-level item
  const hasActiveThirdLevelItem = (subItem: MenuServiceSubItemType) => {
    if (!subItem.subMenuItems || subItem.subMenuItems.length === 0) return false

    const normalizedPathname = stripDashesFromAnyGuids(currentPathname)
    return subItem.subMenuItems.some(
      ({ link }: MenuServiceSubItemType) =>
        normalizedPathname ===
          stripDashesFromAnyGuids(stripQueryParams(link)) ||
        normalizedPathname.startsWith(
          stripDashesFromAnyGuids(stripQueryParams(link))
        )
    )
  }

  // Helper to determine if a sub-item should be highlighted (active itself OR has active third-level item)
  const isSubItemActive = (subItem: MenuServiceSubItemType) => {
    return (
      subItem.link === activeSubMenuLink || hasActiveThirdLevelItem(subItem)
    )
  }

  // Handle sub-item expansion toggle
  const handleSubItemToggle = useCallback(
    (subItemKey: string, event: React.MouseEvent) => {
      event.preventDefault() // Don't follow the sub-item's link
      event.stopPropagation() // Prevent parent click
      setExpandedSubItems(prev => {
        const newSet = new Set(prev)
        if (newSet.has(subItemKey)) {
          newSet.delete(subItemKey)
        } else {
          newSet.add(subItemKey)
        }
        return newSet
      })
    },
    []
  )

  // the arrow's own click handler stops propagation, so a click on it never
  // reaches the entry's anchor
  const handleItemClick = useCallback(
    (item: MenuServiceSubItemType) => (e: React.MouseEvent) =>
      followMenuLink(e, item.link, getProcessedLink, onSubItemClick),
    [getProcessedLink, onSubItemClick]
  )

  const handleSubItemArrowClick = useCallback(
    (subItemKey: string) => (e: React.MouseEvent) => {
      handleSubItemToggle(subItemKey, e)
    },
    [handleSubItemToggle]
  )
  // Use theme from styled-components? Will it break with emotion?
  const theme = useTheme()

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          layoutId="menuPopup"
          style={{
            position: 'fixed',
            top: position.top,
            left: position.left,
            zIndex: tooltipZIndexSelector(theme),
            pointerEvents: 'auto',
            padding: 8,
            marginLeft: -8
          }}
          transition={{
            layout: {
              duration: 0.3,
              ease: 'easeInOut'
            },
            duration: 0.3
          }}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 30 }}
            transition={{ duration: 0.15 }}
          >
            <PopupContainer
              data-testid="left-menu-hover-popup"
              $maxHeight={`calc(100vh - ${position.top + 16}px)`}
            >
              <motion.div
                key={`header-${name}`}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2, delay: 0.05 }}
              >
                <PopupHeaderWithChildren $hasSubItems={subMenuItems.length > 0}>
                  <Typography color="textPrimary" variant="body4">
                    {name}
                  </Typography>
                </PopupHeaderWithChildren>
              </motion.div>

              <AnimatePresence>
                {subMenuItems.length > 0 && (
                  <motion.div
                    key={`submenu-${name}`}
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{
                      duration: 0.15,
                      height: {
                        duration: 0.2,
                        type: 'tween',
                        ease: 'easeInOut'
                      }
                    }}
                    style={{ overflow: 'hidden' }}
                  >
                    <PopupSubItemsContainer>
                      <AnimatePresence>
                        {subMenuItems.map(
                          (subItem: MenuServiceSubItemType, index: number) => {
                            const isActive = isSubItemActive(subItem) // Use new helper that includes parent highlighting
                            const hasThirdLevelItems =
                              subItem.subMenuItems &&
                              subItem.subMenuItems.length > 0
                            const isUserExpanded = expandedSubItems.has(
                              subItem.link || subItem.name
                            )
                            const subItemKey = subItem.link || subItem.name
                            const href = getMenuItemHref(
                              subItem.link,
                              getProcessedLink
                            )

                            return (
                              <React.Fragment
                                key={`popup-sub-fragment-${subItemKey}`}
                              >
                                {/* Second-level item */}
                                <motion.div
                                  key={`popup-sub-item-${subItemKey}`}
                                  initial={{ opacity: 0, x: -10 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  exit={{
                                    opacity: 0,
                                    x: -10,
                                    transition: { duration: 0.1 } // Fast exit
                                  }}
                                  transition={{
                                    duration: 0.2,
                                    delay: index * 0.03 + 0.02
                                  }}
                                >
                                  <PopupSubItemWithChildren
                                    $isActive={isActive}
                                    href={href}
                                    aria-current={isActive ? 'page' : undefined}
                                    onClick={handleItemClick(subItem)}
                                    style={{
                                      cursor: href ? 'pointer' : 'default',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between'
                                    }}
                                  >
                                    <Typography
                                      color={
                                        isActive ? 'primary' : 'textSecondary'
                                      }
                                      variant="body5"
                                    >
                                      {subItem.name}
                                    </Typography>

                                    {/* Arrow for sub-items that have third-level items */}
                                    {hasThirdLevelItems && (
                                      <SubItemArrowContainerWithChildren
                                        className="popup-sub-item-arrow"
                                        onAuxClick={preventLinkFollow}
                                        $hasSubItems={hasThirdLevelItems}
                                        $isOpen={isUserExpanded}
                                        $isParentActive={isActive}
                                        onClick={handleSubItemArrowClick(
                                          subItemKey
                                        )}
                                        style={{ marginRight: 0 }}
                                      >
                                        <ArrowForwardIos />
                                      </SubItemArrowContainerWithChildren>
                                    )}
                                  </PopupSubItemWithChildren>
                                </motion.div>

                                {/* Third-level items */}
                                {hasThirdLevelItems &&
                                  (isUserExpanded ||
                                    hasActiveThirdLevelItem(subItem) ||
                                    searchExpandedSubItems.has(subItemKey)) && (
                                    <AnimatePresence>
                                      {subItem
                                        .subMenuItems!.filter(
                                          (
                                            thirdLevelItem: MenuServiceSubItemType
                                          ) => {
                                            // If user has expanded or search has expanded, show all items
                                            // If collapsed but has active items, show only active items
                                            return (
                                              isUserExpanded ||
                                              searchExpandedSubItems.has(
                                                subItemKey
                                              ) ||
                                              thirdLevelItem.link ===
                                                activeSubMenuLink
                                            )
                                          }
                                        )
                                        .map(
                                          (
                                            thirdLevelItem: MenuServiceSubItemType,
                                            thirdIndex: number
                                          ) => {
                                            const isThirdLevelActive =
                                              thirdLevelItem.link ===
                                              activeSubMenuLink
                                            const thirdLevelHref =
                                              getMenuItemHref(
                                                thirdLevelItem.link,
                                                getProcessedLink
                                              )

                                            return (
                                              <motion.div
                                                key={`popup-third-level-item-${thirdLevelItem.link}-${thirdLevelItem.name}`}
                                                initial={{ opacity: 0, x: -10 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                exit={{
                                                  opacity: 0,
                                                  x: -10,
                                                  transition: { duration: 0.1 }
                                                }}
                                                transition={{
                                                  duration: 0.2,
                                                  delay:
                                                    index * 0.03 +
                                                    thirdIndex * 0.02 +
                                                    0.02
                                                }}
                                              >
                                                <PopupSubItemWithChildren
                                                  $isActive={isThirdLevelActive}
                                                  href={thirdLevelHref}
                                                  aria-current={
                                                    isThirdLevelActive
                                                      ? 'page'
                                                      : undefined
                                                  }
                                                  onClick={handleItemClick(
                                                    thirdLevelItem
                                                  )}
                                                  style={{
                                                    cursor: thirdLevelHref
                                                      ? 'pointer'
                                                      : 'default',
                                                    marginLeft: '16px' // Indent third-level items
                                                  }}
                                                >
                                                  <Typography
                                                    color={
                                                      isThirdLevelActive
                                                        ? 'primary'
                                                        : 'textSecondary'
                                                    }
                                                    variant="body5"
                                                  >
                                                    {thirdLevelItem.name}
                                                  </Typography>
                                                </PopupSubItemWithChildren>
                                              </motion.div>
                                            )
                                          }
                                        )}
                                    </AnimatePresence>
                                  )}
                              </React.Fragment>
                            )
                          }
                        )}
                      </AnimatePresence>
                    </PopupSubItemsContainer>
                  </motion.div>
                )}
              </AnimatePresence>
            </PopupContainer>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default memo(withCommonSnapshotsOnly(HoverPopup))
